import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BedDouble, Loader2, Receipt, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Ipd() {
    const navigate = useNavigate();
    const [admissions, setAdmissions] = useState(null);
    const [status, setStatus] = useState("admitted");

    const load = async () => {
        try {
            const { data } = await api.get("/ipd", { params: { status: status === "all" ? "" : status } });
            setAdmissions(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { load(); }, [status]);

    return (
        <div className="space-y-5 animate-fade-up" data-testid="ipd-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">IPD — Inpatients</h1>
                    <p className="mt-1 text-base text-slate-500">Admissions across the practice</p>
                </div>
                <div className="flex gap-1.5" data-testid="ipd-status-filter">
                    {[["admitted", "Admitted"], ["discharged", "Discharged"], ["all", "All"]].map(([k, l]) => (
                        <button key={k} data-testid={`ipd-filter-${k}`} onClick={() => setStatus(k)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${status === k ? "bg-[#E06D1F] text-white border-[#E06D1F]" : "bg-white text-slate-600 border-slate-300 hover:border-orange-300"}`}>{l}</button>
                    ))}
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ipd-list-card">
                {!admissions ? (
                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : admissions.length === 0 ? (
                    <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="ipd-empty">No {status === "all" ? "" : status} admissions. Admit patients from their profile → IPD tab.</p>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {admissions.map((a) => (
                            <div key={a.id} data-testid={`ipd-row-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                                <div className="flex items-center gap-4">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#E06D1F]"><BedDouble className="h-5 w-5" /></span>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">
                                            {a.patient_name} <span className="font-mono2 text-xs text-[#C2410C]">{a.patient_uid}</span>
                                            <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${a.status === "discharged" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{a.status}</span>
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {a.admission_date} → {a.discharge_date || "present"}{a.ward ? ` · Ward ${a.ward}` : ""} · {a.provisional_diagnosis || a.final_diagnosis || a.reason || "—"}
                                            {a.treating_doctor && <span className="ml-1 inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{a.treating_doctor}</span>}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button data-testid={`ipd-open-${a.id}`} size="sm" variant="outline" onClick={() => navigate(`/patients/${a.patient_id}?tab=ipd`)}>Open Patient</Button>
                                    <Button
                                        data-testid={`ipd-bill-${a.id}`}
                                        size="sm"
                                        variant="outline"
                                        onClick={() => navigate(`/finance?new=1&pid=${a.patient_id}&pname=${encodeURIComponent(a.patient_name)}&desc=${encodeURIComponent(`IPD stay${a.ward ? ` (${a.ward})` : ""} ${a.admission_date} to ${a.discharge_date || "present"}`)}`)}
                                        className="gap-1.5 text-[#C2410C] border-orange-200 hover:bg-[#FFF4EC]"
                                    ><Receipt className="h-3.5 w-3.5" /> Bill</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
